import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { MessageV2Dto } from "@/api/types";
import { getCommunityMessageStatus } from "@/components/CommunityFeed/helpers";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { Pdf } from "@/components/Pdf/Pdf";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Stack } from "@/design-system/Stack";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { twResolve } from "@/helpers/tw-resolve";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

import { ChevronSelectorVerticalIcon } from "../Icons/ChevronSelectorVerticalIcon";
import { Globe02Icon } from "../Icons/Globe02Icon";
import { Video } from "../Video/Video";
import { CommunityPostGallery } from "./CommunityPostMedia/CommunityPostGallery";
import { MentionContent } from "./MentionContent";
import { PromotedGroupCard } from "./PromotedGroupCard";
import { CommunityPostStatus } from "./types";

type TruncationState = "detecting" | "truncated" | "expanded" | "none";

interface CommunityMessageContentProps {
  message: MessageV2Dto;
  isExpanded?: boolean;
  shouldLoad?: boolean;
  onJoinGroup?: ({ groupId }: { groupId: string }) => Promise<void>;
}

export function CommunityMessageContent({
  message,
  isExpanded,
  shouldLoad,
  onJoinGroup,
}: CommunityMessageContentProps): React.ReactNode {
  const hasAutoTranslation = !!message.autoTranslation;
  const [isTranslated, translationHandler] = useBool(() => hasAutoTranslation);

  const { t } = useTranslation();
  const projectId = useProjectId();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const postHog = usePostHog();

  const communityFeedQueries = useCommunityFeedQueries();
  const { data: translation, isLoading: isLoadingTranslation } = useQuery({
    ...communityFeedQueries.messageTranslation({
      messageId: message.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isTranslated && !hasAutoTranslation,
  });
  const updateVideoViewCountMutation = fileMutations.useUpdateVideoViewCount();
  const activeTranslation = isTranslated ? (message.autoTranslation ?? translation) : undefined;
  const rawContent = (activeTranslation?.content ?? message.content)?.trim() || "";

  const handleToggleTranslation = useCallback(() => {
    if (hasAutoTranslation) {
      postHog.capture(
        isTranslated
          ? "community_auto_translation_show_original_clicked"
          : "community_auto_translation_show_translation_clicked",
        { messageId: message.id },
      );
    } else if (!isTranslated) {
      postHog.capture("community_manual_translate_clicked", { messageId: message.id });
    }

    translationHandler.toggle();
  }, [hasAutoTranslation, isTranslated, message.id, postHog, translationHandler]);

  const handleToggleFollowGroup = useCallback(
    (id: string, action: "join" | "leave") => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGE_DETAILS(projectId, message.id) });

      if (action === "join") {
        void onJoinGroup?.({ groupId: id });
      }
    },
    [message.id, onJoinGroup, projectId, queryClient],
  );

  const isOwnPost = message.author.id === sessionUser.id;
  const isTranslatable = sessionUser.autoTranslationLanguage
    ? message.languageIsoCode !== sessionUser.autoTranslationLanguage.id
    : message.languageIsoCode !== sessionUser.language.id;
  const title = activeTranslation?.title ?? message.title;
  const translatedContentTemplate = activeTranslation?.contentTemplate;
  const contentTemplate = activeTranslation?.content == null ? message.contentTemplate : translatedContentTemplate;
  const status = getCommunityMessageStatus(message);
  const { content, entity: attachedEntity } = useMemo(() => unfurlDeepLinkFromContent(rawContent), [rawContent]);

  const [truncationState, setTruncationState] = useState<TruncationState>(isExpanded ? "none" : "detecting");
  const [lastContent, setLastContent] = useState(content);
  const contentRef = useRef<HTMLSpanElement>(null);

  if (!isExpanded && lastContent !== content) {
    setLastContent(content);
    setTruncationState((prev) => {
      if (prev === "expanded") {
        return "expanded";
      } else {
        return "detecting";
      }
    });
  }

  useLayoutEffect(() => {
    if (truncationState !== "detecting") {
      return;
    }

    const el = contentRef.current;
    if (!el) {
      return;
    }

    setTruncationState(el.scrollHeight > el.clientHeight ? "truncated" : "none");
  }, [truncationState]);

  const handleReadMore = useCallback(() => {
    setTruncationState("expanded");
  }, []);

  const canBeTranslated = !hasAutoTranslation && message.author.id !== sessionUser.id && isTranslatable && !!content;
  const showTranslateToggle = (hasAutoTranslation && !!content) || canBeTranslated;
  const documentAttachment = message.documents.at(0);
  const hasAttachment =
    message.images.length > 0 || message.videos.length > 0 || message.documents.length > 0 || attachedEntity != null;

  return (
    <Stack gap="sm">
      <Stack gap="sm">
        <p
          className={twResolve(
            "text-body-emphasis whitespace-pre-line text-text-primary",
            status === CommunityPostStatus.DELETED && "text-text-secondary line-through",
            status === CommunityPostStatus.ARCHIVED && "text-text-secondary",
          )}
          data-testid="post-title"
        >
          {title}
        </p>
        {content && (
          <span
            ref={contentRef}
            className={twResolve(
              "text-text-primary",
              truncationState === "detecting" && "line-clamp-6",
              truncationState === "truncated" && "line-clamp-4",
              message.deletedAt && "text-text-secondary line-through",
              message.archivedAt && "text-text-secondary",
            )}
            data-testid="post-content"
          >
            <MentionContent
              content={content}
              contentTemplate={contentTemplate}
              mentions={message.mentions}
              source="post"
            />
          </span>
        )}
      </Stack>
      {isTranslated && message.autoTranslation?.isAutoTranslated && <AutoTranslatedLabel />}
      {(truncationState === "truncated" || showTranslateToggle) && (
        <nav className="flex flex-row gap-space-lg py-space-sm">
          {truncationState === "truncated" && (
            <Button variant="ghostPrimary" onClick={handleReadMore} icon={<ChevronSelectorVerticalIcon />}>
              {t("component.community-post.content.read-more")}
            </Button>
          )}
          {showTranslateToggle && (
            <Button
              variant="ghostPrimary"
              data-testid="translate-btn"
              isLoading={isLoadingTranslation}
              onClick={handleToggleTranslation}
              icon={<Globe02Icon />}
            >
              {isTranslated
                ? t("component.community-post.content.translate.original")
                : t("component.community-post.content.translate")}
            </Button>
          )}
        </nav>
      )}

      {message.relatedGroup && (
        <PromotedGroupCard
          id={message.relatedGroup.id}
          name={message.relatedGroup.name}
          description={message.relatedGroup.description}
          image={message.relatedGroup.image}
          isResidentGroup={message.relatedGroup.isResidentGroup}
          isOwner={message.relatedGroup.isOwner}
          isMember={message.relatedGroup.isMember}
          followCallback={handleToggleFollowGroup}
        />
      )}
      {hasAttachment && (
        <CommunityContentRail>
          <Stack gap="md">
            {message.images.length > 0 && (
              <figure data-testid="attachment-image">
                <CommunityPostGallery images={message.images} shouldLoad={shouldLoad} />
              </figure>
            )}
            {message.videos.length > 0 && (
              <figure data-testid="attachment-video">
                <Video
                  video={message.videos[0]}
                  onOpen={(videoId) =>
                    updateVideoViewCountMutation.mutate({
                      videoId,
                    })
                  }
                  showViewCount={isOwnPost || sessionUser.isSuperAdmin}
                  shouldLoad={shouldLoad}
                />
              </figure>
            )}
            {attachedEntity && (
              <ContentBlock as="aside" width="flow" align="left" data-testid="attached-entity-preview">
                <EntityPreview
                  entityId={attachedEntity.payload.entityId}
                  entityType={attachedEntity.payload.entityType}
                  otherIds={{
                    folderId: attachedEntity.payload.folderId,
                  }}
                  shouldLoad={shouldLoad}
                  onClick={() =>
                    postHog.capture("clicked_comm_feed_entity_preview", {
                      entity_type: attachedEntity.payload.entityType,
                    })
                  }
                />
              </ContentBlock>
            )}
            {documentAttachment && (
              <figure data-testid="attachment-document">
                <Pdf
                  key={documentAttachment.id}
                  previewImage={documentAttachment.previewImage}
                  fileName={documentAttachment.fileName}
                  fileSize={documentAttachment.fileSize}
                  onClick={() => window.open(documentAttachment.url, "_blank")}
                />
              </figure>
            )}
          </Stack>
        </CommunityContentRail>
      )}
    </Stack>
  );
}
